import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import ArticleIcon from "@mui/icons-material/Article";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import DownloadIcon from "@mui/icons-material/Download";
import { tokens } from "../../../theme";
import { getMockResultSummary } from "../../../data/mockPipelineResultCards";
import {
  tryLoadTier1Card,
  deriveSummaryFromTier1,
  fetchSamplePreviewBlobUrls,
  fetchTier1SummaryJson,
} from "../../../utils/tier1Artifacts";

/**
 * Prototype-only viewer for a backend-generated Tier 1 result card.
 *
 * Layout decision (2026-05-25 v2):
 *   - The inline panel renders a NATIVE React "Model Summary" — KPI cells +
 *     federated meta + a list of what the full card contains. No iframe
 *     inline (inline iframes get cramped, font fallback differs from the
 *     browser, and content gets visually truncated under any reasonable
 *     panel height).
 *   - Clicking "View Full Result Card" opens a 95vw × 95vh modal that
 *     renders the full HTML card inside a sandboxed iframe. There the card
 *     has nearly the full viewport, looks indistinguishable from opening
 *     the HTML file directly in a browser.
 *   - The HTML files have NO <script> tags (verified by grep). We lock the
 *     iframe with `sandbox=""` (empty allow-list = maximum restriction).
 *     Google Fonts still load — they're CSS resources, not scripts.
 *
 * In the real product the inline panel will read from a backend run record
 * and the iframe src will point at an authed artifact URL. Today both are
 * static mocks served from app/public/mock-result-cards/.
 */
// ── Shared section title (used by CM + secondary metrics panels) ─────
const PanelTitle = ({ children, colors }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      mb: 0.75,
      fontWeight: 700,
      fontSize: "0.65rem",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: colors?.grey?.[600] || "var(--fx-text-secondary)",
    }}
  >
    {children}
  </Typography>
);

// ── Confusion matrix panel ───────────────────────────────────────────
// dev-0.7.13: takes the normalized `confusionMatrix` object from
// deriveSummaryFromTier1. Two shapes:
//   - binary : { mode:'binary', cells:{tp,fn,fp,tn} } → 2×2 box
//   - multi  : { mode:'multiclass', labels:[...], matrix:[[...]...] } → N×N grid
// Diagonal cells (correct predictions) tint green; off-diagonal tint
// red with intensity scaled by count. Zero cells stay neutral grey.
const ConfusionMatrixPanel = ({ cm, colors }) => {
  if (!cm) return null;

  // ── binary 2×2 ──
  if (cm.mode === "binary") {
    const { tp, fn, fp, tn } = cm.cells;
    const Cell = ({ value, isCorrect, primary }) => (
      <Box
        sx={{
          p: 1.25,
          minHeight: 76,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${isCorrect ? "var(--fx-success-border)" : "var(--fx-danger-border)"}`,
          bgcolor: isCorrect ? "var(--fx-success-soft)" : "var(--fx-danger-soft)",
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.05rem",
            color: isCorrect ? "var(--fx-success-text)" : "var(--fx-danger-text)",
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.58rem",
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: isCorrect ? "var(--fx-success-text)" : "var(--fx-danger-text)",
            mt: 0.25,
          }}
        >
          {primary}
        </Typography>
      </Box>
    );
    return (
      <Box sx={{ mb: 2 }}>
        <PanelTitle colors={colors}>Confusion matrix (binary)</PanelTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "78px 1fr 1fr",
            columnGap: 0.75,
            rowGap: 0.75,
            alignItems: "stretch",
          }}
        >
          <Box />
          <Box sx={{ textAlign: "center", fontSize: "0.6rem", fontWeight: 700, color: "var(--fx-text-secondary)" }}>
            PREDICTED: POS
          </Box>
          <Box sx={{ textAlign: "center", fontSize: "0.6rem", fontWeight: 700, color: "var(--fx-text-secondary)" }}>
            PREDICTED: NEG
          </Box>
          <Box
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: "var(--fx-text-secondary)",
              alignSelf: "center",
              textAlign: "right",
              pr: 1,
            }}
          >
            ACTUAL: POS
          </Box>
          <Cell value={tp} isCorrect primary="TP" />
          <Cell value={fn} isCorrect={false} primary="FN" />
          <Box
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: "var(--fx-text-secondary)",
              alignSelf: "center",
              textAlign: "right",
              pr: 1,
            }}
          >
            ACTUAL: NEG
          </Box>
          <Cell value={fp} isCorrect={false} primary="FP" />
          <Cell value={tn} isCorrect primary="TN" />
        </Box>
      </Box>
    );
  }

  // ── multi-class N×N ──
  const { labels, matrix } = cm;
  const n = labels.length;
  const flatMax = Math.max(0, ...matrix.flat().filter((v) => typeof v === "number"));
  return (
    <Box sx={{ mb: 2 }}>
      <PanelTitle colors={colors}>
        Confusion matrix ({n}×{n}, multi-class)
      </PanelTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(110px, auto) repeat(${n}, minmax(42px, 1fr))`,
          columnGap: 0.4,
          rowGap: 0.4,
          alignItems: "stretch",
        }}
      >
        {/* Header row: empty corner + column (predicted) labels */}
        <Box />
        {labels.map((l, j) => (
          <Tooltip key={`col-${j}`} title={`Predicted: ${l}`}>
            <Box
              sx={{
                fontSize: "0.58rem",
                fontWeight: 700,
                color: "var(--fx-text-secondary)",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                px: 0.25,
                pb: 0.25,
                borderBottom: "1px solid var(--fx-border)",
              }}
            >
              {l}
            </Box>
          </Tooltip>
        ))}
        {/* Data rows: row label + N cells. Each row is wrapped in a
            React.Fragment with a stable key so React reconciles each
            row's children correctly. We can't use the [<Label/>].concat
            (row.map(...)) trick because the JSX parser misreads the
            arrow-function `const` inside the inner map. */}
        {matrix.map((row, i) => (
          <React.Fragment key={`cm-row-${i}`}>
            <Tooltip title={`Actual: ${labels[i]}`}>
              <Box
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: "var(--fx-text-secondary)",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pr: 1,
                  alignSelf: "center",
                  borderRight: "1px solid var(--fx-border)",
                }}
              >
                {labels[i]}
              </Box>
            </Tooltip>
            {row.map((val, j) => {
              const isDiag = i === j;
              const numericVal = typeof val === "number" ? val : 0;
              const intensity = flatMax > 0 ? numericVal / flatMax : 0;
              const bg = isDiag
                ? `rgba(34, 197, 94, ${0.1 + intensity * 0.55})`
                : numericVal > 0
                ? `rgba(239, 68, 68, ${0.1 + intensity * 0.55})`
                : "var(--fx-subtle)";
              const fg = isDiag
                ? "#14532d"
                : numericVal > 0
                ? "#7f1d1d"
                : "var(--fx-text-muted)";
              const borderColor = isDiag
                ? "rgba(34, 197, 94, 0.45)"
                : numericVal > 0
                ? "rgba(239, 68, 68, 0.35)"
                : "var(--fx-border)";
              return (
                <Tooltip
                  key={`cm-${i}-${j}`}
                  title={`Actual=${labels[i]} · Predicted=${labels[j]} · count=${val}`}
                >
                  <Box
                    sx={{
                      p: 0.5,
                      minHeight: 38,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: bg,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 0.5,
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      color: fg,
                    }}
                  >
                    {val}
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "var(--fx-text-secondary)",
          fontSize: "0.65rem",
          mt: 0.5,
          display: "block",
        }}
      >
        Rows = actual class · Columns = predicted class · Diagonal = correct
      </Typography>
    </Box>
  );
};

// ── Secondary metrics table ──────────────────────────────────────────
// dev-0.7.13: iterates Object.entries(secondary_metrics) verbatim —
// never hardcodes keys. Binary will show precision / recall /
// specificity / accuracy / balanced_accuracy; multi-class will show
// macro_precision / macro_recall / macro_f1 / macro_balanced_accuracy /
// accuracy. The friendly labels are resolved in tier1Artifacts.js.
const SecondaryMetricsTable = ({ metrics, colors }) => {
  if (!Array.isArray(metrics) || metrics.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <PanelTitle colors={colors}>Secondary metrics</PanelTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 0.5,
        }}
      >
        {metrics.map((m) => (
          <Stack
            key={m.key}
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 0.75,
              border: "1px solid var(--fx-border)",
              bgcolor: "var(--fx-paper)",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.72rem", color: "var(--fx-text-secondary)", fontWeight: 500 }}
            >
              {m.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--fx-text-strong)",
              }}
            >
              {m.value}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

// ── Sample predictions panel (dev-0.7.33) ────────────────────────────
// Renders best / median / worst PNG previews for a segmentation Tier1.
// Each card carries:
//   - tier label (Best / Median / Worst)
//   - per-tier score from the Tier1 summary (e.g. soft-dice or whatever
//     metric backend ranked by)
//   - the actual image (via blob URL — see tier1Artifacts.js
//     fetchSamplePreviewBlobUrls for the auth workaround) OR a placeholder
//     when image_status !== "ok".
// Hidden entirely when none of the 3 tiers is present in the input.
const SamplePreviewsPanel = ({ previews, colors }) => {
  if (!previews || typeof previews !== "object") return null;
  const ORDER = ["best", "median", "worst"];
  const LABELS = { best: "Best", median: "Median", worst: "Worst" };
  const present = ORDER.filter((t) => previews[t]);
  if (present.length === 0) return null;

  const placeholderText = (status) => {
    if (status === "metadata_only") {
      // dev-0.7.34 — wording aligned with Backend Agent's HTML card text
      // and the policy semantics: image bytes never leave the site.
      return "Image bytes not transferred, metadata-only privacy mode.";
    }
    if (status === "site_local") {
      // dev-0.7.34 — new policy: PNG rendered + stored site-locally; only
      // metadata + a preview_ref ships to central. Backend retrieval API
      // for the site-local store isn't FE-reachable yet, so this stays
      // a placeholder for the moment (per Backend Agent sync §三).
      return "Image stored at site only, not shared across federation.";
    }
    if (status === "failed") {
      return "Sample image failed to load. Open the full result card for details.";
    }
    return "Image not available.";
  };

  return (
    <Box sx={{ mb: 2 }}>
      <PanelTitle colors={colors}>
        Sample predictions (best / median / worst)
      </PanelTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 1,
        }}
      >
        {ORDER.map((tier) => {
          const p = previews[tier];
          // Reserve the slot even when missing — keeps the 3-column grid
          // visually stable across tiers that haven't reported.
          if (!p) {
            return (
              <Box
                key={tier}
                sx={{
                  border: `1px dashed ${colors.grey?.[800] || "var(--fx-border)"}`,
                  borderRadius: 1,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 140,
                  color: "var(--fx-text-muted)",
                  fontSize: "0.72rem",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {LABELS[tier]}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  No data
                </Typography>
              </Box>
            );
          }
          const scoreText =
            typeof p.score === "number" && Number.isFinite(p.score)
              ? p.score.toFixed(3)
              : "Not set";
          const isOk = p.status === "ok" && p.blobUrl;
          return (
            <Box
              key={tier}
              sx={{
                border: `1px solid ${colors.grey?.[800] || "var(--fx-border)"}`,
                borderRadius: 1,
                p: 1,
                display: "flex",
                flexDirection: "column",
                bgcolor: "var(--fx-paper)",
              }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    color: "var(--fx-text-strong)",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {LABELS[tier]}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.72rem",
                    color: "var(--fx-text-secondary)",
                    fontWeight: 700,
                  }}
                >
                  {scoreText}
                </Typography>
              </Stack>
              {isOk ? (
                <Box
                  component="img"
                  src={p.blobUrl}
                  alt={`${LABELS[tier]} sample prediction`}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 0.5,
                    border: "1px solid var(--fx-subtle)",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    minHeight: 120,
                    bgcolor: "var(--fx-subtle)",
                    border: "1px dashed var(--fx-border)",
                    borderRadius: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--fx-text-secondary)",
                      fontSize: "0.7rem",
                      textAlign: "center",
                      lineHeight: 1.35,
                    }}
                  >
                    {placeholderText(p.status)}
                  </Typography>
                </Box>
              )}
              {p.sampleId && (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.62rem",
                    color: "var(--fx-text-muted)",
                    mt: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={p.sampleId}
                >
                  {p.sampleId}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const KpiCell = ({ cell, colors }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1.5,
      borderRadius: 1.25,
      border: `1px solid ${
        cell.primary ? "rgba(0, 135, 180, 0.4)" : colors.grey?.[800] || "var(--fx-border)"
      }`,
      bgcolor: cell.primary ? "rgba(0, 135, 180, 0.06)" : "var(--fx-paper)",
      minHeight: 92,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: colors.grey?.[500] || "var(--fx-text-muted)",
          fontWeight: 700,
          fontSize: "0.6rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {cell.label}
      </Typography>
      {cell.primary && (
        <Chip
          size="small"
          label="PRIMARY"
          sx={{
            height: 16,
            fontSize: "0.55rem",
            fontWeight: 800,
            letterSpacing: 0.4,
            bgcolor: "rgba(0, 135, 180, 0.15)",
            color: "var(--fx-info-text)",
          }}
        />
      )}
    </Stack>
    <Typography
      sx={{
        fontFamily: cell.text
          ? "ui-monospace, SFMono-Regular, Menlo, monospace"
          : "inherit",
        fontWeight: 800,
        fontSize: cell.text ? "1.05rem" : "1.4rem",
        color: cell.primary ? "var(--fx-info-text)" : "var(--fx-text-strong)",
        lineHeight: 1.1,
      }}
    >
      {cell.value}
    </Typography>
    {cell.hint && (
      <Typography
        variant="caption"
        sx={{ color: colors.grey?.[500] || "var(--fx-text-muted)", mt: 0.25, fontSize: "0.7rem" }}
      >
        {cell.hint}
      </Typography>
    )}
  </Box>
);

const PipelineResultCardViewer = ({
  cardUrl,
  cardLabel,
  pipelineKey,
  taskId,
  // dev-0.7.8 — inline tier1_summary from GET /api/flower_tasks/{task_id}
  // T3 fast-path. When present, headline metrics (best_model / ROC-AUC /
  // PR-AUC / accuracy at recommended threshold / confusion matrix / etc.)
  // come from real backend data instead of mock summary fixtures.
  tier1Summary,
  // Optional dataset display name fallback for the summary heading.
  // tier1Summary.run_metadata may not carry the user-facing dataset
  // name; pass it from the configs flow.
  datasetDisplayName,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const iframeRef = useRef(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Phase F2 dev-0.7.7 — real Tier 1 result card (option F: iframe only,
  // structured-JSON panels deferred). When `taskId` is provided, we try to
  // fetch the real /api/flower_tasks/{task_id}/tier1/Tier1_result_card.html
  // (with Bearer auth, via blob URL so iframe doesn't need to set headers).
  // On 404 / not-yet-ready / any error → null → mock fallback (current
  // `cardUrl` behavior preserved). Once backend's T1+T2 commits deploy,
  // hosted clients flip to real data automatically with no FE change.
  const [realCardBlobUrl, setRealCardBlobUrl] = useState(null);
  useEffect(() => {
    if (!taskId) {
      setRealCardBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdBlobUrl = null;
    (async () => {
      const res = await tryLoadTier1Card(taskId);
      if (cancelled) {
        // Effect was torn down before fetch resolved — revoke any URL
        // we just allocated, otherwise it leaks until tab close.
        if (res?.available && res.cardBlobUrl) {
          URL.revokeObjectURL(res.cardBlobUrl);
        }
        return;
      }
      if (res?.available && res.cardBlobUrl) {
        createdBlobUrl = res.cardBlobUrl;
        setRealCardBlobUrl(res.cardBlobUrl);
      } else {
        setRealCardBlobUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (createdBlobUrl) {
        URL.revokeObjectURL(createdBlobUrl);
      }
    };
  }, [taskId]);

  // Effective iframe src: prefer the real backend card when available,
  // otherwise fall back to the mock `cardUrl`. Same variable is used by
  // both the inline panel CTA (which opens the modal) and the modal
  // iframe, so the switch is atomic.
  const effectiveCardUrl = realCardBlobUrl || cardUrl;
  const isUsingRealCard = !!realCardBlobUrl;

  // dev-0.7.33 — sample preview PNGs (best / median / worst).
  // Mirror the realCardBlobUrl pattern above: authenticatedFetch each PNG,
  // make a blob URL, give it to <img>. The Tier1 modal HTML can't load
  // these directly (Flask requires Bearer; iframe <img> can't set headers;
  // the blob-URL iframe also breaks relative URL resolution). The panel
  // re-derives the metadata from the most recent tier1_summary every time
  // it changes (which only happens when polling lands a new state).
  //
  // Each per-tier entry resolves to:
  //   { status: "ok"|"metadata_only"|"failed", score, sampleId, blobUrl }
  // null means the tier1_summary has no sample_previews block (e.g.
  // classification runs).
  const [samplePreviews, setSamplePreviews] = useState(null);
  const samplePreviewsMeta = tier1Summary?.sample_previews || null;
  // Stable identity key so we don't re-fetch when the parent re-renders
  // with a semantically-equal payload. Comparing JSON.stringify is overkill
  // for short metadata objects but bullet-proof against React state churn.
  const samplePreviewsMetaKey = useMemo(
    () => (samplePreviewsMeta ? JSON.stringify(samplePreviewsMeta) : null),
    [samplePreviewsMeta]
  );
  useEffect(() => {
    if (!taskId) {
      setSamplePreviews(null);
      return undefined;
    }
    let cancelled = false;
    const createdBlobUrls = [];
    (async () => {
      // dev-0.7.33 diagnostic: log what we receive at every step so we
      // can pin down whether backend ships sample_previews inline,
      // whether the standalone JSON has it, and whether PNG fetches
      // succeed. Reverse-engineering this from console fetch attempts
      // hit CORS preflight 404 repeatedly; this in-app log uses
      // authenticatedFetch (same path the page polls with) and avoids
      // the CORS issue entirely.
      console.log('[FX-DIAG-SAMPLE] effect fired for taskId=', taskId);
      console.log('[FX-DIAG-SAMPLE] inline tier1Summary?.sample_previews =',
        samplePreviewsMeta);

      let effectiveMeta = samplePreviewsMeta;

      // Fallback: backend may not inline sample_previews in the polling
      // response but DOES write it to the standalone Tier1_result_summary.json
      // file. Fetch directly when inline is missing.
      if (!effectiveMeta) {
        console.log('[FX-DIAG-SAMPLE] inline missing, trying Tier1_result_summary.json');
        const jsonBody = await fetchTier1SummaryJson(taskId);
        if (cancelled) return;
        console.log('[FX-DIAG-SAMPLE] Tier1_result_summary.json body =', jsonBody);
        effectiveMeta = jsonBody?.sample_previews || null;
        console.log('[FX-DIAG-SAMPLE] effectiveMeta from JSON fallback =', effectiveMeta);
      }

      if (!effectiveMeta) {
        console.log('[FX-DIAG-SAMPLE] no sample_previews anywhere; bailing.');
        if (!cancelled) setSamplePreviews(null);
        return;
      }

      console.log('[FX-DIAG-SAMPLE] fetching PNG blobs for tiers =',
        Object.keys(effectiveMeta.tiers || {}));
      const result = await fetchSamplePreviewBlobUrls(taskId, effectiveMeta);
      console.log('[FX-DIAG-SAMPLE] fetchSamplePreviewBlobUrls returned =', result);

      if (cancelled) {
        if (result) {
          for (const tier of Object.values(result)) {
            if (tier && tier.blobUrl) URL.revokeObjectURL(tier.blobUrl);
          }
        }
        return;
      }
      if (result) {
        for (const tier of Object.values(result)) {
          if (tier && tier.blobUrl) createdBlobUrls.push(tier.blobUrl);
        }
      }
      console.log('[FX-DIAG-SAMPLE] final samplePreviews state set =', result);
      setSamplePreviews(result || null);
    })();
    return () => {
      cancelled = true;
      for (const url of createdBlobUrls) {
        URL.revokeObjectURL(url);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, samplePreviewsMetaKey]);

  // dev-0.7.8 — prefer the real T3 tier1_summary when present, otherwise
  // fall back to the mock summary fixture. `derivedSummary` mirrors the
  // mock shape (title / dataset / subtitle / heroMetrics / federatedMeta /
  // thresholdNote / labelDef / includes), so downstream UI code below
  // doesn't care which source produced it.
  const derivedSummary = tier1Summary
    ? deriveSummaryFromTier1(tier1Summary, datasetDisplayName)
    : null;
  const summary = derivedSummary || getMockResultSummary(pipelineKey);
  const isUsingRealSummary = !!derivedSummary;

  // Reset iframe loading state each time the modal opens with a fresh key.
  useEffect(() => {
    if (modalOpen) {
      setIframeLoading(true);
      setIframeError(false);
    }
  }, [modalOpen, iframeKey]);

  if (!effectiveCardUrl) return null;

  const handleReload = () => {
    setIframeKey((k) => k + 1);
  };

  const heroCells = summary?.heroMetrics || [];

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: `1px solid ${colors.grey?.[800] || "var(--fx-border)"}`,
          borderRadius: 2,
          boxShadow: 'var(--fx-shadow-sm)',
          bgcolor: "var(--fx-paper)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <ArticleIcon sx={{ color: colors.secondary?.[500] || "#0087B4" }} />
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: colors.primary?.[500] || "var(--fx-text-strong)" }}
                  >
                    Pipeline Result Card
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: colors.grey?.[600] || "var(--fx-text-secondary)" }}>
                  {cardLabel || "Backend-generated artifact (mock)"}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Title + subtitle row (taken from card hero) */}
          {summary && (
            <Box sx={{ mt: 1.5, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "var(--fx-text-strong)", fontSize: "1.1rem", lineHeight: 1.2 }}
              >
                {summary.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", color: colors.grey?.[600] || "var(--fx-text-secondary)", mt: 0.3 }}
              >
                <strong style={{ color: "var(--fx-text-strong)" }}>{summary.dataset}</strong>
                {summary.subtitle ? ` · ${summary.subtitle}` : ""}
              </Typography>
            </Box>
          )}

          {/* KPI hero grid — 1 / 2 / 4 columns responsive */}
          {heroCells.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: `repeat(${heroCells.length}, 1fr)`,
                },
                gap: 1.25,
                mb: 2,
              }}
            >
              {heroCells.map((cell) => (
                <KpiCell key={cell.label} cell={cell} colors={colors} />
              ))}
            </Box>
          )}

          {/* dev-0.7.13 — Confusion matrix panel (binary 2×2 or multi N×N).
              Source: tier1_summary.metrics_at_recommended_threshold.confusion_matrix.
              Shape detected by deriveConfusionMatrix in tier1Artifacts.js;
              null on mock-summary path so nothing renders for legacy fixtures. */}
          {summary?.confusionMatrix && (
            <ConfusionMatrixPanel cm={summary.confusionMatrix} colors={colors} />
          )}

          {/* dev-0.7.13 — Secondary metrics table (iterates Object.entries,
              never hardcodes keys). Source:
              tier1_summary.metrics_at_recommended_threshold.secondary_metrics. */}
          {summary?.secondaryMetrics?.length > 0 && (
            <SecondaryMetricsTable metrics={summary.secondaryMetrics} colors={colors} />
          )}

          {/* dev-0.7.33 — Sample predictions (best / median / worst) panel.
              Source: tier1_summary.sample_previews. PNG blobs fetched via
              authenticatedFetch in the useEffect above (iframe <img>
              can't carry the Bearer header and the modal HTML loads via
              blob URL anyway, so a side-by-side React-side renderer is
              the only way to surface these images authenticated). Hidden
              when previews is null (classification runs / pre-Tier1). */}
          {samplePreviews && (
            <SamplePreviewsPanel previews={samplePreviews} colors={colors} />
          )}

          {/* Federated meta line */}
          {summary?.federatedMeta && (
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1.25 }}>
              <Chip
                size="small"
                label={`Strategy · ${summary.federatedMeta.strategy}`}
                sx={metaChipSx}
              />
              <Chip
                size="small"
                label={`Rounds · ${summary.federatedMeta.rounds}`}
                sx={metaChipSx}
              />
              <Chip
                size="small"
                label={`Clients · ${summary.federatedMeta.clients}`}
                sx={metaChipSx}
              />
              <Chip
                size="small"
                label={`Local epochs · ${summary.federatedMeta.localEpochs}`}
                sx={metaChipSx}
              />
            </Stack>
          )}

          {/* Threshold / loss note */}
          {summary?.thresholdNote && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.25,
                color: colors.grey?.[600] || "var(--fx-text-secondary)",
                fontStyle: "italic",
              }}
            >
              {summary.thresholdNote}
              {summary.labelDef ? ` · ${summary.labelDef}` : ""}
            </Typography>
          )}

          {/* What the full card includes */}
          {summary?.includes?.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "var(--fx-subtle)",
                border: `1px dashed ${colors.grey?.[800] || "var(--fx-border)"}`,
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  color: colors.grey?.[600] || "var(--fx-text-secondary)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "0.6rem",
                  mb: 0.75,
                }}
              >
                The full result card also includes
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {summary.includes.map((item) => (
                  <Typography
                    key={item}
                    component="li"
                    variant="caption"
                    sx={{
                      display: "list-item",
                      color: "var(--fx-text-secondary)",
                      fontSize: "0.78rem",
                      lineHeight: 1.55,
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Disclaimer — switches text based on whether we resolved a real
              Tier1 artifact. Real-data path was added in dev-0.7.7 (T1+T2);
              dev-0.7.13 expanded the structured panels above to cover both
              binary and multi-class classification. */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1.5,
              color: colors.grey?.[500] || "var(--fx-text-muted)",
              fontStyle: "italic",
              fontSize: "0.7rem",
            }}
          >
            {isUsingRealSummary || isUsingRealCard
              ? "Real Tier 1 artifact, values are read live from /api/flower_tasks/{task_id}/tier1 for this completed federated run."
              : "Prototype mock, values and the linked HTML card represent the kind of backend-generated artifact that will be attached to a completed federated run. No real artifact endpoint is queried."}
          </Typography>

          {/* Actions */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              startIcon={<LaunchIcon />}
              onClick={() => setModalOpen(true)}
              sx={{
                bgcolor: colors.secondary?.[500] || "#0087B4",
                "&:hover": { bgcolor: colors.secondary?.[600] || "#006a8c" },
                fontWeight: 700,
              }}
            >
              View Full Result Card
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              component="a"
              href={effectiveCardUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </Button>
            {/* dev-0.7.46 — Download HTML. Uses the same effectiveCardUrl
                that powers the iframe + new-tab buttons. For real Tier1
                runs, effectiveCardUrl is a blob URL produced by
                authenticatedFetch (the only way to bypass the Bearer-auth
                wall on /api/flower_tasks/<id>/tier1/*). For mock fallback
                cards it's the static /mock-result-cards/... asset URL.
                The download attribute carries the filename through to the
                browser save dialog in both cases. We bake a short task_id
                prefix into the filename to match the backend-Content-
                Disposition naming convention (federatex_<id前8>_...). */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              component="a"
              href={effectiveCardUrl}
              download={
                taskId
                  ? `federatex_${String(taskId).slice(0, 8)}_Tier1_result_card.html`
                  : 'Tier1_result_card.html'
              }
            >
              Download HTML
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Fullscreen modal — 95vw / 95vh — full HTML card rendered inside */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            maxWidth: "95vw",
            height: "95vh",
            m: 0,
            overflow: "hidden",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            px: 2.5,
            py: 1.25,
            borderBottom: `1px solid ${colors.grey?.[800] || "var(--fx-border)"}`,
            bgcolor: "#fafafa",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <ArticleIcon sx={{ color: colors.secondary?.[500] || "#0087B4" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, color: "var(--fx-text-strong)", lineHeight: 1.2 }}
                noWrap
              >
                Pipeline Result Card · {summary?.title || cardLabel}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.grey?.[600] || "var(--fx-text-secondary)" }}
                noWrap
              >
                {effectiveCardUrl}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Reload the iframe">
              <span>
                <IconButton size="small" onClick={handleReload} disabled={iframeLoading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open in a new tab">
              <IconButton
                size="small"
                component="a"
                href={effectiveCardUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setModalOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            bgcolor: "var(--fx-paper)",
          }}
        >
          {iframeLoading && !iframeError && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                p: 3,
                bgcolor: "var(--fx-paper)",
              }}
            >
              <Skeleton variant="text" width="40%" height={28} />
              <Skeleton variant="text" width="30%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={120} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={200} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
            </Box>
          )}
          {iframeError && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                textAlign: "center",
                bgcolor: "var(--fx-warning-soft)",
              }}
            >
              <Typography variant="body2" sx={{ color: "var(--fx-warning-text)", fontWeight: 700, mb: 1 }}>
                Could not load the result card
              </Typography>
              <Typography variant="caption" sx={{ color: "#78350f", mb: 2 }}>
                The mock artifact at <code>{effectiveCardUrl}</code> didn&apos;t load.
              </Typography>
              <Button size="small" variant="outlined" onClick={handleReload}>
                Try again
              </Button>
            </Box>
          )}
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={effectiveCardUrl}
            title="Pipeline result card (mock)"
            // Empty sandbox = maximum restriction. The bundled HTML cards
            // have no <script> tags so this is safe. NEVER add allow-scripts
            // without re-reviewing the source HTML — they're authored by
            // backend and could change.
            sandbox=""
            loading="lazy"
            onLoad={() => setIframeLoading(false)}
            onError={() => {
              setIframeLoading(false);
              setIframeError(true);
            }}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              border: "0",
              backgroundColor: "var(--fx-paper)",
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const metaChipSx = {
  height: 22,
  bgcolor: "var(--fx-subtle)",
  color: "var(--fx-text-secondary)",
  fontWeight: 600,
  fontSize: "0.7rem",
  border: "1px solid var(--fx-border)",
};

export default PipelineResultCardViewer;
